/**
 * Ready-made billing periods for the finance filters.
 *
 * The API takes a plain date range rather than a preset name, so an arbitrary
 * range can be added later without touching the contract. Resolving the preset
 * here also means it follows the user's own calendar: "текущий месяц" is the
 * month they are looking at, not the month on the server.
 */
export type PeriodPreset =
	| 'all'
	| 'thisMonth'
	| 'lastMonth'
	| 'thisQuarter'
	| 'thisYear'

export const PERIOD_PRESETS: PeriodPreset[] = [
	'thisMonth',
	'lastMonth',
	'thisQuarter',
	'thisYear',
]

export interface PeriodRange {
	periodFrom?: string
	periodTo?: string
}

/** Local calendar date as YYYY-MM-DD — never toISOString, which shifts to UTC. */
function toIsoDate(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Both bounds are inclusive days, and the server matches by overlap: an
 * invoice running from late May into June belongs to June as much as to May.
 */
export function periodRange(preset: PeriodPreset, now = new Date()): PeriodRange {
	const year = now.getFullYear()
	const month = now.getMonth()

	switch (preset) {
		case 'all':
			return {}
		case 'thisMonth':
			return {
				periodFrom: toIsoDate(new Date(year, month, 1)),
				// Day 0 of the next month is the last day of this one, which
				// spares a table of month lengths and gets February right.
				periodTo: toIsoDate(new Date(year, month + 1, 0)),
			}
		case 'lastMonth':
			return {
				periodFrom: toIsoDate(new Date(year, month - 1, 1)),
				periodTo: toIsoDate(new Date(year, month, 0)),
			}
		case 'thisQuarter': {
			const firstMonth = Math.floor(month / 3) * 3
			return {
				periodFrom: toIsoDate(new Date(year, firstMonth, 1)),
				periodTo: toIsoDate(new Date(year, firstMonth + 3, 0)),
			}
		}
		case 'thisYear':
			return {
				periodFrom: toIsoDate(new Date(year, 0, 1)),
				periodTo: toIsoDate(new Date(year, 11, 31)),
			}
	}
}
